import { FC } from 'react';

import { Link, usePage } from '@inertiajs/react';

import { CUSTOMER_LINKS, SOCIAL_LINKS } from './siteNavigation';
import logoDark from '../../assets/logo-dark-theme.svg';
import logoLight from '../../assets/logo-light-theme.svg';
import { useResolvedTheme } from '../../hooks/useResolvedTheme';
import { CookieConsentService } from '../../services/CookieConsentService';
import { Universe } from '../../types';

type PageProps = {
    universes: Universe[];
};

type Props = {
    onNavigate?: () => void;
    tcgListTestId?: string;
};

export const SiteFooterNavigation: FC<Props> = ({
    onNavigate,
    tcgListTestId,
}) => {
    const { universes } = usePage<PageProps>().props;
    const resolvedTheme = useResolvedTheme();
    const logo = resolvedTheme === 'dark' ? logoDark : logoLight;

    const handleCookieReview = () => {
        onNavigate?.();
        CookieConsentService.requestReview();
    };

    return (
        <div className="grid grid-cols-2 gap-6 sm:gap-8 md:grid-cols-4">
            <div>
                <h3 className="mb-3 text-sm font-semibold sm:mb-4 sm:text-base">
                    TCGs
                </h3>
                <ul
                    className="space-y-1 sm:space-y-2"
                    data-testid={tcgListTestId}
                >
                    {universes.map((universe) => (
                        <li key={universe.id || universe.name}>
                            <Link
                                href={universe.url_path}
                                className="text-xs text-muted-foreground hover:text-foreground sm:text-sm"
                                onClick={onNavigate}
                            >
                                {universe.name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            <div>
                <h3 className="mb-3 text-sm font-semibold sm:mb-4 sm:text-base">
                    Соцмережі
                </h3>
                <ul className="space-y-1 sm:space-y-2">
                    {SOCIAL_LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground sm:text-sm"
                                onClick={onNavigate}
                            >
                                <link.icon className="h-4 w-4" />
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>

            <div>
                <h3 className="mb-3 text-sm font-semibold sm:mb-4 sm:text-base">
                    Клієнтам
                </h3>
                <ul className="space-y-1 sm:space-y-2">
                    {CUSTOMER_LINKS.map((link) => (
                        <li key={link.href}>
                            <Link
                                href={link.href}
                                className="text-xs text-muted-foreground hover:text-foreground sm:text-sm"
                                onClick={onNavigate}
                            >
                                {link.label}
                            </Link>
                        </li>
                    ))}
                    <li>
                        <button
                            className="text-left text-xs text-muted-foreground hover:cursor-pointer hover:text-foreground sm:text-sm"
                            onClick={handleCookieReview}
                            type="button"
                        >
                            Cookies
                        </button>
                    </li>
                </ul>
            </div>

            <div>
                <div className="mb-3 sm:mb-4">
                    <img
                        src={logo}
                        alt="CartOn Logo"
                        width={120}
                        height={32}
                        className="h-6 w-auto sm:h-7"
                    />
                </div>
                <div className="space-y-4">
                    <p className="text-xs text-muted-foreground sm:text-sm">
                        CartOn — це платформа для купівлі, продажу та обміну
                        колекційними картками в Україні. Ми створюємо простий і
                        зручний сервіс для фанатів One Piece TCG, Pokémon, MTG,
                        Yu-Gi-Oh! та інших карткових ігор.
                    </p>
                    <p className="text-xs text-muted-foreground sm:text-sm">
                        Мета CartOn — об&#39;єднати українських колекціонерів і
                        дати можливість легко знаходити та продавати рідкісні
                        картки прямо у своєму місті.
                    </p>
                </div>
            </div>
        </div>
    );
};
